import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {type RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useState} from 'react'

/**
 * Measure the content height of a textarea as a CSS `calc()` expression (scrollHeight minus vertical
 * padding). Kept as a module-scope helper so it carries no reactive dependencies and the effects below
 * can stay referentially stable.
 */
function measureContentHeight(element: HTMLTextAreaElement): string {
  const scrollHeight = element.scrollHeight
  const {paddingTop, paddingBottom} = getComputedStyle(element)
  return `calc(${scrollHeight}px - ${paddingTop} - ${paddingBottom})`
}

type UseDynamicTextareaHeightSettings = {
  disabled?: boolean
  minHeightLines?: number
  maxHeightLines?: number
  elementRef: RefObject<HTMLTextAreaElement | null>
  /** The current value of the input. */
  value: string
}

/**
 * Calculates the optimal height of the textarea according to its content, automatically
 * resizing it as the user types. If the user manually resizes the textarea, their setting
 * will be respected.
 *
 * Returns an object to spread to the component's `style` prop. If you are using `Textarea`,
 * apply this to the child `textarea` element: `<Textarea style={{resultOfThisHook}} />`.
 *
 * NOTE: for the most accurate results, be sure that the `lineHeight` of the element is
 * explicitly set in CSS.
 */
export const useDynamicTextareaHeight = ({
  disabled,
  minHeightLines,
  maxHeightLines,
  elementRef,
  value,
}: UseDynamicTextareaHeightSettings): {
  height: string | undefined
  minHeight: string | undefined
  maxHeight: string | undefined
  boxSizing: 'content-box'
  fieldSizing: 'content'
  overflowWrap: 'anywhere'
} => {
  const [height, setHeight] = useState<string | undefined>(undefined)
  const {isBrowser} = useRuntimeEnvironment()
  const supportsFieldSizingContent = useMemo(() => {
    try {
      return isBrowser ? CSS.supports('field-sizing', 'content') : true
    } catch {
      // If the browser doesn't support CSS.supports, we'll just assume it doesn't support `field-sizing: content` and fall back to our manual height calculations.
      return false
    }
  }, [isBrowser])

  // We only need to measure and apply an explicit height on browsers that lack `field-sizing: content`.
  // When it's supported (the common case on modern browsers), the textarea auto-sizes natively via the
  // returned `fieldSizing` style, so there is nothing to measure.
  const needsManualMeasurement = !disabled && !supportsFieldSizingContent

  // Only subscribe to `value` (and therefore re-measure on every keystroke) when manual measurement is
  // actually required. When it isn't, this resolves to a constant, so the layout effect below runs once
  // and never re-runs while typing — no per-keystroke layout effect, reflow, or commit.
  const measuredValue = needsManualMeasurement ? value : null

  // Stable across keystrokes (its deps don't change while typing): it self-gates on the feature-support /
  // disabled flags and reads the live element value, so neither effect below needs to read `value`.
  const measureAndCommit = useCallback(() => {
    if (disabled || supportsFieldSizingContent) return

    const element = elementRef.current
    // If the value is empty, we don't need to calculate a dynamic height
    if (!element || !element.value) return

    const nextHeight = measureContentHeight(element)
    setHeight(nextHeight)
  }, [disabled, elementRef, supportsFieldSizingContent])

  useLayoutEffect(() => {
    // `measuredValue` only changes when manual measurement is needed and the value is non-empty, so when
    // field-sizing is supported (or the input is empty) this effect runs once and never again while typing.
    if (!measuredValue) return
    measureAndCommit()
  }, [measureAndCommit, measuredValue])

  // With Slots, initial render of the component is delayed and so the initial layout effect can occur
  // before the target element has actually been calculated in the DOM. But if we only use regular effects,
  // there will be a visible flash on initial render when not using slots. This pass (which runs after paint)
  useEffect(() => {
    measureAndCommit()
  }, [measureAndCommit])

  return {
    height,
    minHeight: minHeightLines ? `${minHeightLines}lh` : undefined,
    maxHeight: maxHeightLines ? `${maxHeightLines}lh` : undefined,
    boxSizing: 'content-box',
    fieldSizing: 'content',
    // `overflow-wrap: anywhere` should be the default behavior for textareas, but `field-sizing: content` appears to
    // break that expectation. To ensure that long words still break instead of expanding the textarea horizontally,
    // we must set this explicitly.
    overflowWrap: 'anywhere',
  }
}
